import { cloneState } from './rules';
import type { AiLevel, Color, GameSettings, GameState, InfiniteChessSaveV1 } from './types';

const GAME_KEY = 'infinite-chess:game:v1';
const SETTINGS_KEY = 'infinite-chess:settings:v1';

export const DEFAULT_SETTINGS: GameSettings = {
  playerColor: 'w',
  aiLevel: 1200,
  sound: true,
  haptic: true,
  reducedMotion: typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches,
  nightMode: false,
  flipped: false,
};

function isColor(value: unknown): value is Color {
  return value === 'w' || value === 'b';
}

function isAiLevel(value: unknown): value is AiLevel {
  return value === 900 || value === 1200 || value === 1500 || value === 1800;
}

function validState(value: unknown): value is GameState {
  if (!value || typeof value !== 'object') return false;
  const state = value as Partial<GameState>;
  return Array.isArray(state.board)
    && state.board.length === 64
    && isColor(state.turn)
    && !!state.castling
    && typeof state.rngState === 'number'
    && Array.isArray(state.moveHistory)
    && !!state.positionCounts
    && typeof state.status === 'string';
}

export function saveGame(state: GameState, settings: GameSettings): void {
  const save: InfiniteChessSaveV1 = {
    version: 1,
    savedAt: new Date().toISOString(),
    state,
    settings,
  };
  localStorage.setItem(GAME_KEY, JSON.stringify(save));
}

export function loadGame(): InfiniteChessSaveV1 | null {
  try {
    const parsed: unknown = JSON.parse(localStorage.getItem(GAME_KEY) ?? 'null');
    if (!parsed || typeof parsed !== 'object') return null;
    const save = parsed as Partial<InfiniteChessSaveV1>;
    if (save.version !== 1 || typeof save.savedAt !== 'string' || !validState(save.state)) return null;
    return {
      version: 1,
      savedAt: save.savedAt,
      state: cloneState(save.state),
      settings: normalizeSettings(save.settings),
    };
  } catch {
    return null;
  }
}

export function clearSave(): void {
  localStorage.removeItem(GAME_KEY);
}

function normalizeSettings(value: Partial<GameSettings> | undefined): GameSettings {
  const input = value ?? {};
  return {
    ...DEFAULT_SETTINGS,
    ...input,
    playerColor: isColor(input.playerColor) ? input.playerColor : DEFAULT_SETTINGS.playerColor,
    aiLevel: isAiLevel(input.aiLevel) ? input.aiLevel : DEFAULT_SETTINGS.aiLevel,
    sound: typeof input.sound === 'boolean' ? input.sound : DEFAULT_SETTINGS.sound,
    haptic: typeof input.haptic === 'boolean' ? input.haptic : DEFAULT_SETTINGS.haptic,
    reducedMotion: typeof input.reducedMotion === 'boolean' ? input.reducedMotion : DEFAULT_SETTINGS.reducedMotion,
    nightMode: typeof input.nightMode === 'boolean' ? input.nightMode : DEFAULT_SETTINGS.nightMode,
    flipped: typeof input.flipped === 'boolean' ? input.flipped : DEFAULT_SETTINGS.flipped,
  };
}

// Day/night choice shared with the Play index and the other games on this origin.
const SHARED_THEME_KEY = 'pandu-play:theme';

export function withSharedTheme(settings: GameSettings): GameSettings {
  try {
    const theme = localStorage.getItem(SHARED_THEME_KEY);
    if (theme === 'night' || theme === 'day') return { ...settings, nightMode: theme === 'night' };
  } catch {
    // Storage unavailable: keep the game's own setting.
  }
  return settings;
}

export function loadSettings(): GameSettings {
  try {
    const parsed: unknown = JSON.parse(localStorage.getItem(SETTINGS_KEY) ?? 'null');
    return withSharedTheme(normalizeSettings(parsed as Partial<GameSettings> | undefined));
  } catch {
    return withSharedTheme({ ...DEFAULT_SETTINGS });
  }
}

export function saveSettings(settings: GameSettings): void {
  localStorage.setItem(SETTINGS_KEY, JSON.stringify(settings));
  localStorage.setItem(SHARED_THEME_KEY, settings.nightMode ? 'night' : 'day');
}
